import React, { useState, useEffect } from 'react';
import { api } from '../api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SyncDiagnosticsTab = ({ config, onUpdate }) => {
  const [syncing, setSyncing] = useState(false);
  const [diagnosticsRunning, setDiagnosticsRunning] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [diagnosticsResult, setDiagnosticsResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Load HOAs for ledger lookup dropdown
  useEffect(() => {
    const loadHOAs = async () => {
      try {
        const response = await api.get('/api/user-scanner/hoas');
        const hoas = response.data.hoas || [];
        
        const select = document.getElementById('hoaSelectLedger');
        if (select) {
          // Clear existing options except first
          select.innerHTML = '<option value="">Select HOA...</option>';
          
          // Add HOAs
          hoas.forEach(hoa => {
            const option = document.createElement('option');
            option.value = hoa.id;
            option.textContent = hoa.name;
            select.appendChild(option);
          });
        }
      } catch (error) {
        console.error('Error loading HOAs:', error);
      }
    };
    
    // Load HOAs after component mounts
    setTimeout(loadHOAs, 500);
  }, []);

  const handleSyncSubscription = async () => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);

    try {
      const response = await api.post(
        `/api/platform-admin/hoa/${config.hoa.id}/sync-subscription`,
        {}
      );

      setSyncResult(response.data);
      
      // Trigger parent refresh to reload HOA configuration and subscription data
      if (onUpdate) {
        await onUpdate();
      }
      
      // Show user to switch to Subscription tab to see updates
      setTimeout(() => {
        if (window.confirm('✅ Sync complete!\n\nWould you like to view the updated Subscription tab?')) {
          // This would need to be passed from parent to switch tabs
          // For now, just notify user
        }
      }, 500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to sync subscription');
    } finally {
      setSyncing(false);
    }
  };

  const handleRunDiagnostics = async () => {
    setDiagnosticsRunning(true);
    setError(null);
    setDiagnosticsResult(null);

    try {
      const response = await api.post(
        `/api/platform-admin/hoa/${config.hoa.id}/run-diagnostics`,
        {}
      );

      setDiagnosticsResult(response.data.diagnostics);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to run diagnostics');
    } finally {
      setDiagnosticsRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          🔧 Sync & Diagnostics
        </h3>
        <p className="text-sm text-blue-700">
          Use these tools to manually sync subscription data from Stripe and diagnose configuration issues.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800">❌ {error}</p>
        </div>
      )}

      {/* Sync Subscription Section */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-1">
              Sync Stripe Subscription
            </h4>
            <p className="text-sm text-gray-600">
              Pull the latest subscription data from Stripe and enable corresponding features in the HOA.
            </p>
          </div>
          <button
            onClick={handleSyncSubscription}
            disabled={syncing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors whitespace-nowrap ml-4"
          >
            {syncing ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing...
              </span>
            ) : (
              '🔄 Sync Now'
            )}
          </button>
        </div>

        {/* Sync Results */}
        {syncResult && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="font-medium text-green-900 mb-2">✅ {syncResult.message}</p>
            
            {syncResult.actions_taken && syncResult.actions_taken.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-green-800 mb-2">Actions Taken:</p>
                <ul className="space-y-1">
                  {syncResult.actions_taken.map((action, idx) => (
                    <li key={idx} className="text-sm text-green-700 pl-4">
                      • {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {syncResult.subscription && (
              <div className="mt-3 pt-3 border-t border-green-300">
                <p className="text-sm font-medium text-green-800 mb-2">Current Subscription:</p>
                <div className="grid grid-cols-2 gap-2 text-sm text-green-700">
                  <div>Plan: <span className="font-medium">{syncResult.subscription.plan_tier}</span></div>
                  <div>Add-ons: <span className="font-medium">{syncResult.subscription.addons?.length || 0}</span></div>
                  {syncResult.subscription.addons && syncResult.subscription.addons.length > 0 && (
                    <div className="col-span-2">
                      Active: <span className="font-medium">{syncResult.subscription.addons.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Run Diagnostics Section */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-1">
              Run Diagnostics
            </h4>
            <p className="text-sm text-gray-600">
              Check HOA configuration for common issues and missing data.
            </p>
          </div>
          <button
            onClick={handleRunDiagnostics}
            disabled={diagnosticsRunning}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors whitespace-nowrap ml-4"
          >
            {diagnosticsRunning ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Running...
              </span>
            ) : (
              '🔍 Run Diagnostics'
            )}
          </button>
        </div>

        {/* Diagnostics Results */}
        {diagnosticsResult && (
          <div className="mt-4 space-y-3">
            {/* Issues */}
            {diagnosticsResult.issues && diagnosticsResult.issues.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-red-900 mb-2">Issues Found:</p>
                <ul className="space-y-1">
                  {diagnosticsResult.issues.map((issue, idx) => (
                    <li key={idx} className="text-sm text-red-700">
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {diagnosticsResult.warnings && diagnosticsResult.warnings.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="font-medium text-yellow-900 mb-2">Warnings:</p>
                <ul className="space-y-1">
                  {diagnosticsResult.warnings.map((warning, idx) => (
                    <li key={idx} className="text-sm text-yellow-700">
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Info */}
            {diagnosticsResult.info && diagnosticsResult.info.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="font-medium text-blue-900 mb-2">System Info:</p>
                <ul className="space-y-1">
                  {diagnosticsResult.info.map((info, idx) => (
                    <li key={idx} className="text-sm text-blue-700">
                      {info}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* All Clear */}
            {(!diagnosticsResult.issues || diagnosticsResult.issues.length === 0) &&
             (!diagnosticsResult.warnings || diagnosticsResult.warnings.length === 0) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm font-medium text-green-800">
                  ✅ No critical issues found
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Ledger Property Lookup & Delete - REMOVE STUCK PROPERTIES */}
      <div className="bg-white border rounded-lg p-6 mt-6">
        <div className="mb-4">
          <h4 className="text-md font-semibold text-gray-900 mb-1">
            🏠 Ledger Property Lookup & Delete
          </h4>
          <p className="text-sm text-gray-600 mb-3">
            Search for and completely remove properties from the ledger system (including all transactions).
          </p>
          
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <select
                id="hoaSelectLedger"
                className="px-3 py-2 border rounded-lg"
                style={{minWidth: '200px'}}
              >
                <option value="">Select HOA...</option>
              </select>
              <input
                type="text"
                placeholder="Enter property address..."
                className="flex-1 px-3 py-2 border rounded-lg"
                id="propertyAddressInput"
              />
              <button
                onClick={async () => {
                  const hoaId = document.getElementById('hoaSelectLedger').value;
                  const propertyAddress = document.getElementById('propertyAddressInput').value.trim();
                  
                  if (!hoaId) {
                    alert('Please select an HOA');
                    return;
                  }
                  
                  if (!propertyAddress) {
                    alert('Please enter a property address');
                    return;
                  }
                  
                  try {
                    const response = await api.post('/api/user-scanner/ledger/search', null, {
                      params: { hoa_id: hoaId, property_address: propertyAddress }
                    });
                    
                    const results = response.data.results;
                    const summary = results.summary;
                    
                    let message = `🏠 LEDGER SEARCH: ${propertyAddress}\n\n`;
                    message += `📊 Summary:\n`;
                    message += `• Found in ${summary.total_locations} location(s)\n\n`;
                    
                    if (results.found_in.length > 0) {
                      message += `📍 Locations:\n${results.found_in.join(', ')}\n\n`;
                      
                      if (results.details.ledger_entries) {
                        message += `💰 Ledger: ${results.details.ledger_entries.count} transaction(s)\n`;
                        message += `   Unpaid Balance: $${results.details.ledger_entries.total_unpaid_balance.toFixed(2)}\n\n`;
                      }
                      
                      message += `⚠️ Click "Delete Property" to permanently remove from ledger.`;
                    } else {
                      message += `✅ Property not found in ledger.`;
                    }
                    
                    alert(message);
                    console.log('Detailed search results:', response.data);
                    
                    // Store for delete
                    document.getElementById('propertyAddressInput').dataset.searchedProperty = propertyAddress;
                    document.getElementById('propertyAddressInput').dataset.searchedHoa = hoaId;
                  } catch (error) {
                    console.error('Error searching property:', error);
                    alert('❌ Search failed: ' + (error.response?.data?.detail || error.message));
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                🔍 Search
              </button>
              <button
                onClick={async () => {
                  const hoaId = document.getElementById('propertyAddressInput').dataset.searchedHoa || 
                               document.getElementById('hoaSelectLedger').value;
                  const propertyAddress = document.getElementById('propertyAddressInput').value.trim() ||
                                         document.getElementById('propertyAddressInput').dataset.searchedProperty;
                  
                  if (!hoaId || !propertyAddress) {
                    alert('Please search for a property first');
                    return;
                  }
                  
                  if (!confirm(`🚨 DESTRUCTIVE ACTION WARNING!\n\nThis will PERMANENTLY DELETE:\n• Property: ${propertyAddress}\n• All ledger transactions\n• All manual account records\n• Summary balances\n\nUsers with this property address will NOT be deleted.\n\nThis CANNOT be undone!\n\nAre you absolutely sure?`)) {
                    return;
                  }
                  
                  try {
                    const response = await api.post('/api/user-scanner/ledger/delete', null, {
                      params: { hoa_id: hoaId, property_address: propertyAddress }
                    });
                    
                    const results = response.data.results;
                    
                    let message = `✅ PROPERTY DELETED!\n\n`;
                    message += `Actions Taken:\n`;
                    results.actions_taken.forEach(action => {
                      message += `• ${action}\n`;
                    });
                    
                    alert(message);
                    console.log('Delete results:', response.data);
                    
                    // Clear input
                    document.getElementById('propertyAddressInput').value = '';
                    delete document.getElementById('propertyAddressInput').dataset.searchedProperty;
                    delete document.getElementById('propertyAddressInput').dataset.searchedHoa;
                  } catch (error) {
                    console.error('Error deleting property:', error);
                    alert('❌ Delete failed: ' + (error.response?.data?.detail || error.message));
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                🗑️ Delete Property
              </button>
            </div>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded">
          <p className="text-xs text-orange-800">
            <strong>⚠️ WARNING:</strong> Delete removes property from: manual_accounts, ledger_entries, ledgers_summary<br/>
            <strong>Preserves:</strong> User accounts (they may still exist as users)<br/>
            <strong>Use Case:</strong> Stuck properties that can't be deleted via normal UI
          </p>
        </div>
      </div>

      {/* User Email Scanner - SEARCH & CLEANUP STUCK USERS */}
      <div className="bg-white border rounded-lg p-6 mt-6">
        <div className="mb-4">
          <h4 className="text-md font-semibold text-gray-900 mb-1">
            🔍 User Email Scanner
          </h4>
          <p className="text-sm text-gray-600 mb-3">
            Search for any user email across the entire platform and clean up stuck references.
          </p>
          
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="Enter email address to scan..."
              className="flex-1 px-3 py-2 border rounded-lg"
              id="emailScanInput"
            />
            <button
              onClick={async () => {
                const email = document.getElementById('emailScanInput').value.trim();
                if (!email) {
                  alert('Please enter an email address');
                  return;
                }
                
                try {
                  const response = await api.post('/api/user-scanner/scan', null, {
                    params: { email }
                  });
                  
                  const results = response.data.results;
                  const summary = results.summary;
                  const details = results.details;
                  
                  let message = `🔍 SCAN RESULTS FOR: ${email}\n\n`;
                  message += `📊 Summary:\n`;
                  message += `• Found in ${summary.total_locations} location(s)\n`;
                  message += `• Active users: ${summary.active_users}\n`;
                  message += `• Archived users: ${summary.archived_users}\n`;
                  message += `• Stuck in settings: ${summary.stuck_in_settings}\n\n`;
                  
                  if (results.found_in.length > 0) {
                    message += `━━━━━━━━━━━━━━━━━━━━━━\n`;
                    message += `📍 DETAILED LOCATIONS:\n`;
                    message += `━━━━━━━━━━━━━━━━━━━━━━\n\n`;
                    
                    // Show users with HOA info
                    if (details.users && details.users.length > 0) {
                      message += `👤 ACTIVE USERS (${details.users.length}):\n`;
                      details.users.forEach((u, i) => {
                        message += `${i + 1}. HOA: ${u.hoa_id}\n`;
                        message += `   Role: ${u.role}\n`;
                        message += `   Status: ${u.status}\n`;
                        message += `   Property: ${u.property || 'None'}\n\n`;
                      });
                    }
                    
                    // Show voting settings
                    if (details.voting_settings && details.voting_settings.length > 0) {
                      message += `🗳️ VOTING SETTINGS (${details.voting_settings.length} HOA(s)):\n`;
                      details.voting_settings.forEach((vs, i) => {
                        message += `${i + 1}. HOA: ${vs.hoa_id}\n`;
                        message += `   Issues: ${vs.issues.join(', ')}\n\n`;
                      });
                    }
                    
                    // Show newsletter settings
                    if (details.newsletter_settings && details.newsletter_settings.length > 0) {
                      message += `📧 NEWSLETTER SETTINGS (${details.newsletter_settings.length} HOA(s)):\n`;
                      details.newsletter_settings.forEach((ns, i) => {
                        message += `${i + 1}. HOA: ${ns.hoa_id}\n\n`;
                      });
                    }
                    
                    // Show manual accounts
                    if (details.manual_accounts && details.manual_accounts.length > 0) {
                      message += `📋 MANUAL ACCOUNTS (${details.manual_accounts.length}):\n`;
                      details.manual_accounts.forEach((m, i) => {
                        message += `${i + 1}. HOA: ${m.hoa_id}\n`;
                        message += `   Property: ${m.property}\n\n`;
                      });
                    }
                    
                    // Show archived
                    if (details.archived_users && details.archived_users.length > 0) {
                      message += `🗄️ ARCHIVED (${details.archived_users.length}):\n`;
                      details.archived_users.forEach((a, i) => {
                        message += `${i + 1}. HOA: ${a.hoa_id}\n`;
                        message += `   Removed: ${a.removed_at}\n\n`;
                      });
                    }
                    
                    message += `━━━━━━━━━━━━━━━━━━━━━━\n\n`;
                    message += `⚠️ OPTIONS:\n`;
                    message += `• "Clean Up All" - Removes from ALL HOAs\n`;
                    message += `• Or check console for HOA IDs to remove selectively`;
                  } else {
                    message += `✅ Email not found anywhere in the system.`;
                  }
                  
                  alert(message);
                  console.log('📋 DETAILED SCAN RESULTS:', response.data);
                  console.log('💡 TIP: To remove from specific HOA, note the HOA ID above');
                  
                  // Store full results for selective cleanup
                  document.getElementById('emailScanInput').dataset.scannedEmail = email;
                  document.getElementById('emailScanInput').dataset.scanResults = JSON.stringify(results);
                } catch (error) {
                  console.error('Error scanning email:', error);
                  alert('❌ Scan failed: ' + (error.response?.data?.detail || error.message));
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              🔍 Scan
            </button>
            <button
              onClick={async () => {
                const email = document.getElementById('emailScanInput').value.trim() || 
                             document.getElementById('emailScanInput').dataset.scannedEmail;
                const scanResultsStr = document.getElementById('emailScanInput').dataset.scanResults;
                
                if (!email || !scanResultsStr) {
                  alert('Please scan an email first to see where they are located');
                  return;
                }
                
                const scanResults = JSON.parse(scanResultsStr);
                const details = scanResults.details;
                
                // Build list of HOAs this email is in
                let hoaIds = new Set();
                if (details.users) {
                  details.users.forEach(u => hoaIds.add(u.hoa_id));
                }
                if (details.voting_settings) {
                  details.voting_settings.forEach(vs => hoaIds.add(vs.hoa_id));
                }
                if (details.newsletter_settings) {
                  details.newsletter_settings.forEach(ns => hoaIds.add(ns.hoa_id));
                }
                if (details.manual_accounts) {
                  details.manual_accounts.forEach(m => hoaIds.add(m.hoa_id));
                }
                
                const hoaList = Array.from(hoaIds);
                
                if (hoaList.length === 0) {
                  alert('No HOAs found for this email');
                  return;
                }
                
                if (hoaList.length === 1) {
                  alert(`Email is only in 1 HOA (${hoaList[0]}). Use "Clean Up All" button or specify HOA ID in console.`);
                  return;
                }
                
                // Ask user which HOA to remove from
                const hoaId = prompt(`Email is in ${hoaList.length} HOA(s):\n\n${hoaList.map((id, i) => `${i + 1}. ${id}`).join('\n')}\n\nEnter the HOA ID to remove from (or "all" for all HOAs):`);
                
                if (!hoaId) {
                  return;
                }
                
                if (hoaId.toLowerCase() === 'all') {
                  // Use full cleanup
                  if (!confirm(`⚠️ Remove ${email} from ALL ${hoaList.length} HOAs?`)) {
                    return;
                  }
                  
                  try {
                    const response = await api.post('/api/user-scanner/cleanup', null, {
                      params: { email }
                    });
                    
                    const results = response.data.results;
                    let message = `✅ REMOVED FROM ALL HOAs!\n\nActions:\n`;
                    results.actions_taken.forEach(action => {
                      message += `• ${action}\n`;
                    });
                    alert(message);
                  } catch (error) {
                    alert('❌ Cleanup failed: ' + (error.response?.data?.detail || error.message));
                  }
                } else {
                  // Remove from specific HOA
                  if (!hoaList.includes(hoaId)) {
                    alert(`HOA ID "${hoaId}" not found in scan results. Please enter exact ID from list above.`);
                    return;
                  }
                  
                  if (!confirm(`⚠️ Remove ${email} from HOA:\n${hoaId}\n\nThey will remain in other HOAs.\n\nContinue?`)) {
                    return;
                  }
                  
                  try {
                    const response = await api.post('/api/user-scanner/cleanup-from-hoa', null, {
                      params: { email, hoa_id: hoaId }
                    });
                    
                    const results = response.data.results;
                    let message = `✅ REMOVED FROM HOA: ${hoaId}\n\nActions:\n`;
                    results.actions_taken.forEach(action => {
                      message += `• ${action}\n`;
                    });
                    alert(message);
                  } catch (error) {
                    alert('❌ Cleanup failed: ' + (error.response?.data?.detail || error.message));
                  }
                }
                
                // Clear after cleanup
                document.getElementById('emailScanInput').value = '';
                delete document.getElementById('emailScanInput').dataset.scannedEmail;
                delete document.getElementById('emailScanInput').dataset.scanResults;
              }}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              🎯 Remove from HOA
            </button>
            <button
              onClick={async () => {
                const email = document.getElementById('emailScanInput').value.trim() || 
                             document.getElementById('emailScanInput').dataset.scannedEmail;
                             
                if (!email) {
                  alert('Please scan an email first');
                  return;
                }
                
                if (!confirm(`⚠️ WARNING: This will permanently remove ${email} from ALL HOAs:\n\n• Active users\n• All committees\n• Poll access lists\n• Newsletter access lists\n• Manual accounts\n\nArchived records and ledger entries will be preserved.\n\nContinue with FULL cleanup?`)) {
                  return;
                }
                
                try {
                  const response = await api.post('/api/user-scanner/cleanup', null, {
                    params: { email }
                  });
                  
                  const results = response.data.results;
                  
                  let message = `✅ CLEANUP COMPLETE!\n\n`;
                  message += `Actions Taken:\n`;
                  results.actions_taken.forEach(action => {
                    message += `• ${action}\n`;
                  });
                  
                  if (results.preserved.length > 0) {
                    message += `\n📝 Preserved (for audit):\n`;
                    results.preserved.forEach(item => {
                      message += `• ${item}\n`;
                    });
                  }
                  
                  alert(message);
                  console.log('Cleanup results:', response.data);
                  
                  // Clear input
                  document.getElementById('emailScanInput').value = '';
                  delete document.getElementById('emailScanInput').dataset.scannedEmail;
                  delete document.getElementById('emailScanInput').dataset.scanResults;
                } catch (error) {
                  console.error('Error cleaning up email:', error);
                  alert('❌ Cleanup failed: ' + (error.response?.data?.detail || error.message));
                }
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              🗑️ Clean Up All
            </button>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-xs text-red-800">
            <strong>Scans for email in:</strong> users, archived_users, voting_settings (committees, poll access), newsletter_settings, ledger_entries, manual_accounts<br/>
            <strong>Cleanup removes from:</strong> Active users, committees, poll lists, newsletter lists, manual accounts<br/>
            <strong>Preserves:</strong> Archived records (audit), Ledger entries (financial)
          </p>
        </div>
      </div>

      {/* Sync Membership Permissions - FIX ACCESS ISSUES */}
      <div className="bg-white border rounded-lg p-6 mt-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-1">
              👥 Sync Membership Permissions (Fix Access Issues)
            </h4>
            <p className="text-sm text-gray-600">
              Fix access permissions across ALL HOAs for polls, newsletters, communications tab, and committee features.
            </p>
          </div>
          <button
            onClick={async () => {
              if (!confirm('This will sync and fix membership permissions across ALL HOAs. This ensures users have correct access to polls, newsletters, and communications. Continue?')) {
                return;
              }
              
              try {
                const response = await api.post('/api/vote/sync-all-permissions');
                
                const summary = response.data.summary || {};
                
                let message = `✅ Permission Sync Complete!\n\n`;
                message += `HOAs Processed: ${summary.hoas_processed || 0}\n`;
                message += `Poll Permissions Fixed: ${summary.poll_permissions_fixed || 0}\n`;
                message += `Committee Access Fixed: ${summary.committee_access_fixed || 0}\n`;
                message += `Total Users Granted Access: ${summary.total_users_granted || 0}\n`;
                
                if (summary.issues_found > 0) {
                  message += `\n⚠️ Issues Found & Fixed: ${summary.issues_found}`;
                }
                
                if (summary.errors > 0) {
                  message += `\n❌ Errors: ${summary.errors}`;
                }
                
                alert(message);
                console.log('Permission Sync Results:', response.data);
              } catch (error) {
                console.error('Error syncing permissions:', error);
                alert('❌ Sync failed: ' + (error.response?.data?.detail || error.message));
              }
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap ml-4"
          >
            👥 Sync Permissions
          </button>
        </div>
        
        <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded">
          <p className="text-xs text-indigo-800">
            <strong>What this fixes:</strong><br/>
            • Poll creation access (board_only, board_and_committee, specific_users)<br/>
            • Newsletter access for committee members<br/>
            • Communications/Scheduler tab access<br/>
            • Removes invalid committee members<br/>
            • Validates all permission settings match actual users
          </p>
        </div>
      </div>

      {/* SUPER POWER: Find & Register ALL Files */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 rounded-lg p-6 mt-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-lg font-bold text-red-900 mb-2">
              🚨 EMERGENCY: Find & Register ALL Files
            </h4>
            <p className="text-sm text-red-800 font-medium mb-2">
              Use this if documents show 404 errors. Scans ENTIRE filesystem and creates database records for EVERY file found.
            </p>
            <p className="text-xs text-red-700">
              This is the MOST POWERFUL tool - finds files even without any database records. Use when documents exist but can't be viewed.
            </p>
          </div>
          <button
            onClick={async () => {
              if (!confirm('🔧 SURGICAL FIX: This will ONLY fix documents that have 404 errors.\n\nIt will:\n• Find documents with broken file paths\n• Search for the actual files on disk\n• Fix the database records\n• NOT create duplicates\n• NOT touch other HOAs\n\nContinue?')) {
                return;
              }
              
              try {
                const response = await api.post('/api/document-404-fix/fix-404-errors');
                
                const totalChecked = response.data.total_checked || 0;
                const fixed = response.data.fixed || 0;
                const alreadyOk = response.data.already_ok || 0;
                const notFound = response.data.not_found || 0;
                
                let message = `🔧 404 FIX COMPLETE!\n\n`;
                message += `Total Documents Checked: ${totalChecked}\n`;
                message += `✅ Fixed 404 Errors: ${fixed}\n`;
                message += `✓ Already Working: ${alreadyOk}\n`;
                message += `❌ Files Permanently Lost: ${notFound}\n\n`;
                message += response.data.message || '';
                
                alert(message);
                console.log('Fix 404 Results:', response.data);
              } catch (error) {
                console.error('Error fixing 404s:', error);
                alert('❌ Fix failed: ' + (error.response?.data?.detail || error.message));
              }
            }}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap ml-4 font-bold shadow-lg"
          >
            🔧 FIX 404 ERRORS ONLY
          </button>
        </div>
        
        <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
          <p className="text-xs text-blue-900 font-medium">
            🔧 Surgical Fix - SAFE & PRECISE:
          </p>
          <ul className="text-xs text-blue-800 mt-2 space-y-1 ml-4">
            <li>✓ Only fixes documents that have 404 errors</li>
            <li>✓ Searches for actual files on disk by document ID</li>
            <li>✓ Updates broken database records with correct paths</li>
            <li>✓ Does NOT create duplicates</li>
            <li>✓ Does NOT touch files from other HOAs</li>
            <li>✓ Does NOT create new records - only fixes existing ones</li>
          </ul>
        </div>
      </div>

      {/* Restore Documents Section - FIX 404 ERRORS */}
      <div className="bg-white border rounded-lg p-6 mt-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-1">
              🔧 Comprehensive Document Sync (Fix ALL Issues)
            </h4>
            <p className="text-sm text-gray-600">
              Scans EVERY file on disk and EVERY database record. Finds documents by ID across all locations, creates missing records, fixes wrong paths, and syncs visibility. Solves all 404 errors.
            </p>
          </div>
          <button
            onClick={async () => {
              if (!confirm('This will scan ALL files on disk and ALL database records, then fix any mismatches. This may take a few moments. Continue?')) {
                return;
              }
              
              try {
                const response = await api.post('/api/comprehensive-sync/fix-all-documents?sync_to_s3=true');
                
                const filesScanned = response.data.files_scanned || 0;
                const filesCreated = response.data.files_created || 0;
                const filesPathFixed = response.data.files_path_fixed || 0;
                const filesVisibilityFixed = response.data.files_visibility_fixed || 0;
                const filesSyncedToS3 = response.data.files_synced_to_s3 || 0;
                const s3Status = response.data.s3_status || 'unknown';
                const totalFixes = response.data.total_fixes || 0;
                
                let message = `✅ Comprehensive Document Sync Complete!\n\n`;
                message += `Files Scanned: ${filesScanned}\n`;
                message += `Records Created: ${filesCreated}\n`;
                message += `Paths Fixed: ${filesPathFixed}\n`;
                message += `Visibility Fixed: ${filesVisibilityFixed}\n`;
                message += `Synced to S3: ${filesSyncedToS3} (Status: ${s3Status})\n`;
                message += `Total Fixes: ${totalFixes}\n\n`;
                message += response.data.message || '';
                
                if (s3Status === 'not_configured') {
                  message += '\n\n⚠️ AWS S3 not configured. Documents remain on local disk.';
                  message += '\nConfigure AWS in Platform Admin to enable automatic S3 backup.';
                }
                
                alert(message);
                console.log('Comprehensive Sync Results:', response.data);
              } catch (error) {
                console.error('Error in comprehensive sync:', error);
                alert('❌ Sync failed: ' + (error.response?.data?.detail || error.message));
              }
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap ml-4"
          >
            🔧 Fix All Documents
          </button>
        </div>
        
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
          <p className="text-xs text-green-800">
            <strong>What this fixes:</strong><br/>
            • Files on disk with no database records (creates them)<br/>
            • Database records with wrong file paths (fixes them)<br/>
            • Documents searchable by ID across ALL locations<br/>
            • Wrong mime_types for images<br/>
            • Visibility mismatches (all_homeowners vs public)<br/>
            • <strong>Migrates final documents to AWS S3 for permanent storage</strong><br/>
            • Works even after environment forks/redeployments
          </p>
        </div>
      </div>

      {/* Public Documents Sync Section - PLATFORM ADMIN ONLY */}
      <div className="bg-white border rounded-lg p-6 mt-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-1">
              📄 Sync Public Documents (Mark as Public)
            </h4>
            <p className="text-sm text-gray-600">
              Mark documents ALREADY SELECTED in Public Page Settings as "public" visibility. Does NOT change user selections.
            </p>
          </div>
          <button
            onClick={async () => {
              if (!confirm('This will mark documents ALREADY SELECTED in Public Page Settings as publicly viewable. Your selections will NOT be changed. Continue?')) {
                return;
              }
              
              try {
                const response = await api.post('/api/public-documents-sync/platform-admin/sync-all-hoas');
                
                alert(`✅ Sync Complete!\n\nHOAs Processed: ${response.data.hoas_processed}\nDocuments Marked Public: ${response.data.total_documents_synced}\n\nYour selected documents are now viewable on public pages.`);
                
                console.log('Public Documents Sync Results:', response.data);
              } catch (error) {
                console.error('Error syncing public documents:', error);
                alert('❌ Sync failed: ' + (error.response?.data?.detail || error.message));
              }
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap ml-4"
          >
            🔄 Sync Public Documents
          </button>
        </div>
        
        <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded">
          <p className="text-xs text-purple-800">
            <strong>What this does:</strong> Reads documents you've ALREADY selected in each HOA's Public Page Settings and marks them as "public" visibility so they can be viewed on the public page. Does NOT change which documents are selected.
          </p>
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-gray-50 border rounded-lg p-4 mt-6">
        <p className="text-sm text-gray-700">
          <strong>When to use Sync:</strong> If a new HOA signed up but their features aren't enabled, 
          or if you manually changed subscription settings and need to apply them to the HOA.
        </p>
        <p className="text-sm text-gray-700 mt-2">
          <strong>When to use Diagnostics:</strong> To quickly check if an HOA has all required 
          configuration (subscription, public profile, users, etc.).
        </p>
      </div>
    </div>
  );
};

export default SyncDiagnosticsTab;
